import type {
  LoanDto,
  LoanListResponse,
  CreateLoanRequest,
  UpdateLoanBalanceRequest,
  SetLoanGuarantorsRequest,
  SetLoanAgreementRequest,
} from '@signalvote/shared';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite, edit } from '../../lib/data';

/** The server's sentence for a refusal (an empty one counts as none), else the fallback. */
const refusal = (fallback: string) => (body: ApiErrorBody, response: Response) =>
  failureFromResponse(response, body, typeof body.error === 'string' && body.error !== '' ? body.error : fallback);

// FEC-009 (D10) — the candidate personal-funds loan manual-entry surface (Schedule C).
// The loan is a living treasurer-maintained record: recorded once, then its
// outstandingBalance / paidToDate are updated as repayments are made (a repayment is a
// separate SB19A expenditure; v1 does NOT auto-link the repayment bank-line). Backs the
// federal Form 3 Schedule A SA13A receipt + Schedule C SC/10 ledger.

/** The campaign's loans, newest incur date first. */
export const useFinanceLoans = defineQuery({
  key: () => ['finance', 'loans'],
  reads: 'GET /api/finance/loans',
  fetch: async (_: undefined, { campaignId }): Promise<LoanDto[]> => {
    const body = await apiJson<LoanListResponse>('/api/finance/loans', {
      headers: campaignHeaders(campaignId),
      error: 'Failed to load loans',
    });
    return body.items;
  },
});

/** Show a loan voided (or restored) in the list before the server answers. */
const markVoided = (id: string, voided: boolean) => [
  edit(useFinanceLoans, undefined, (loans) =>
    loans.map((l) => (l.id === id ? { ...l, voidedAtUtc: voided ? new Date().toISOString() : null } : l)),
  ),
];

/** POST /api/finance/loans — record a new candidate loan (balance seeds to the principal). */
export const useCreateLoan = defineWrite({
  send: (data: CreateLoanRequest) =>
    apiJson<LoanDto>('/api/finance/loans', { method: 'POST', json: data, error: refusal('Failed to record loan') }),
});

/**
 * PUT /api/finance/loans/{id}/guarantors — AFC-004: replace a loan's guarantor set
 * (up to 3, the AL EDI v6.0 cap). Each guarantor emits an `lnGuarantor{N}_*` block on
 * the AL `<receipt reReceiptType=07>` at file time.
 */
export const useSetLoanGuarantors = defineWrite({
  send: ({ id, data }: { id: string; data: SetLoanGuarantorsRequest }) =>
    apiJson<LoanDto>(`/api/finance/loans/${id}/guarantors`, {
      method: 'PUT',
      json: data,
      error: refusal('Failed to save guarantors'),
    }),
});

/**
 * PUT /api/finance/loans/{id}/agreement — GitHub #73: set a federal loan's Schedule C-1
 * loan-agreement details (secured/collateral, lending-representative name + title, and the
 * treasurer / lender signature dates). Emitting these in-app retires the FEC-portal hand-off
 * for the loan agreement. Mirrors `useSetLoanGuarantors`: the request carries the full
 * agreement snapshot and the server returns the updated `LoanDto`.
 */
export const useSetLoanAgreement = defineWrite({
  send: ({ id, data }: { id: string; data: SetLoanAgreementRequest }) =>
    apiJson<LoanDto>(`/api/finance/loans/${id}/agreement`, {
      method: 'PUT',
      json: data,
      error: refusal('Failed to save loan agreement'),
    }),
});

/** PATCH /api/finance/loans/{id}/balance — update the running ledger after a repayment. */
export const useUpdateLoanBalance = defineWrite({
  send: ({ id, data }: { id: string; data: UpdateLoanBalanceRequest }) =>
    apiJson<LoanDto>(`/api/finance/loans/${id}/balance`, {
      method: 'PATCH',
      json: data,
      error: refusal('Failed to update loan balance'),
    }),
});

/**
 * POST /api/finance/loans/{id}/void. Voids a loan entered in error so it drops
 * from a fresh filing's Schedule C / line 10 and from a federal amendment, while the
 * original filing's frozen record stays intact. No concurrency token (mirrors the other
 * loan writes). A loan in a filed, still-locked period returns a 400 the caller surfaces.
 */
export const useVoidLoan = defineWrite({
  send: (id: string) =>
    apiJson<LoanDto>(`/api/finance/loans/${id}/void`, { method: 'POST', error: refusal('Failed to void loan') }),
  patch: (id) => markVoided(id, true),
});

/** POST /api/finance/loans/{id}/unvoid. Restores a voided loan (the inverse). */
export const useUnvoidLoan = defineWrite({
  send: (id: string) =>
    apiJson<LoanDto>(`/api/finance/loans/${id}/unvoid`, { method: 'POST', error: refusal('Failed to restore loan') }),
  patch: (id) => markVoided(id, false),
});
